import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { EVENT_TYPE, LocationType, Match, Player, SpecielMatchReferee, TeamType } from "../../types/types";

// Define a type for the slice state
export interface dbMatchUpdate {
  id: string;
  matchUpdate: Match;
}

export interface KnockoutMatchUpdate {
  id: keyof KnockOutMatches;
  pulje: string;
  matchUpdate: Match;
}

export interface KnockoutMatchesUpdate {
  puljeId: string;
  matches: KnockOutMatches;
}
export interface MatchUpdate {
  startTime: number;
  endTime: number;
  status: number;
  location: LocationType;
  homeTeam: TeamType;
  awayTeam: TeamType;
}

export interface KnockOutMatches {
  r16_1?: Match;
  r16_2?: Match;
  r16_3?: Match;
  r16_4?: Match;
  r16_5?: Match;
  r16_6?: Match;
  r16_7?: Match;
  r16_8?: Match;
  q_1?: Match;
  q_2?: Match;
  q_3?: Match;
  q_4?: Match;
  s_1?: Match;
  s_2?: Match;
  t?: Match;
  f?: Match;
}
export interface KnockOutTournament {
  [pulje: string]: KnockOutMatches;
}

// Define a type for the slice state
interface MatchesState {
  groupMatches: Array<Match>;
  knockOutTournament: KnockOutTournament;
  editingMatch?: Match;
  lastUpdatedMatch?: Match;
}

// Define the initial state using that type
const initialState: MatchesState = {
  groupMatches: [],
  knockOutTournament: {},
  editingMatch: undefined,
  lastUpdatedMatch: undefined,
};

export const MatchesSlice = createSlice({
  name: "matches",
  initialState,
  reducers: {
    setGroupMatches: (state, action: PayloadAction<Array<Match>>) => {
      state.groupMatches = action.payload;
    },
    addEvent: (
      state,
      action: PayloadAction<{
        match: string;
        team: string;
        id: string;
        eventNumber: number;
        eventType: EVENT_TYPE;
      }>
    ) => {
      state.groupMatches = state.groupMatches.map((match) => {
        if (match.id === action.payload.match) {
          if (action.payload.team === match.homeTeam?.id) {
            return {
              ...match,
              homeTeamEvents: [
                ...match.homeTeamEvents,
                {
                  id: action.payload.id,
                  match,
                  team: match.homeTeam,
                  eventNumber: action.payload.eventNumber,
                  eventType: action.payload.eventType,
                  player: null,
                },
              ],
            };
          } else if (action.payload.team === match.awayTeam?.id) {
            return {
              ...match,
              awayTeamEvents: [
                ...match.awayTeamEvents,
                {
                  id: action.payload.id,
                  match,
                  team: match.awayTeam,
                  eventNumber: action.payload.eventNumber,
                  eventType: action.payload.eventType,
                  player: null,
                },
              ],
            };
          } else {
            return match;
          }
        } else {
          return match;
        }
      });
    },
    updateEvent: (
      state,
      action: PayloadAction<{
        matchId: string;
        eventId: string;
        player: Player;
        eventNumber: number;
      }>
    ) => {
      state.groupMatches = state.groupMatches.map((match) => {
        if (match.id === action.payload.matchId) {
          return {
            ...match,
            homeTeamEvents: match.homeTeamEvents.map((event) => {
              if (event.id === action.payload.eventId) {
                return {
                  ...event,
                  player: action.payload.player,
                  eventNumber: action.payload.eventNumber,
                };
              }
              return event;
            }),
            awayTeamEvents: match.awayTeamEvents.map((event) => {
              if (event.id === action.payload.eventId) {
                return {
                  ...event,
                  player: action.payload.player,
                  eventNumber: action.payload.eventNumber,
                };
              }
              return event;
            }),
          };
        } else {
          return match;
        }
      });
    },
    deleteEvent: (state, action: PayloadAction<{ id: string }>) => {
      state.groupMatches = state.groupMatches.map((match) => {
        return {
          ...match,
          homeTeamEvents: match.homeTeamEvents.filter(
            (event) => event.id !== action.payload.id
          ),
          awayTeamEvents: match.awayTeamEvents.filter(
            (event) => event.id !== action.payload.id
          ),
        };
      });
    },

    updateGroupMatch: (state, action: PayloadAction<Match>) => {
      state.groupMatches = state.groupMatches.map((_match) => {
        if (_match.id === action.payload.id) {
          return {
            ..._match,
            homeTeam: action.payload.homeTeam,
            awayTeam: action.payload.awayTeam,
            startTime: action.payload.startTime,
            endTime: action.payload.endTime,
            status: action.payload.status,
            location: action.payload.location,
            duration: action.payload.duration,
            homeTeamLabel: action.payload.homeTeamLabel,
            awayTeamLabel: action.payload.awayTeamLabel,
          };
        } else {
          return _match;
        }
      });
      state.lastUpdatedMatch = action.payload;
    },
    updateReferees: (state, action: PayloadAction<{ matchId: string; referees: Array<SpecielMatchReferee> }>) => {
      state.groupMatches = state.groupMatches.map((_match) => {
        if (_match.id === action.payload.matchId) {
          return {
            ..._match,
            referees: action.payload.referees,
          };
        } else {
          return _match;
        }
      });
    },
    createGroupMatch: (state, action: PayloadAction<Match>) => {
      state.groupMatches = [...state.groupMatches, action.payload];
    },
    deleteGroupMatch: (state, action: PayloadAction<string>) => {
      state.groupMatches = state.groupMatches.filter(
        (match) => match.id !== action.payload
      );
    },
    setEditingMatch: (state, action: PayloadAction<Match | undefined>) => {
      state.editingMatch = action.payload;
    },
    updateLocationOnAllMatches: (
      state,
      action: PayloadAction<{ locationId: string; delay: number }>
    ) => {
      state.groupMatches = state.groupMatches.map((match) => {
        if (match.location.id === action.payload.locationId) {
          return {
            ...match,
            location: {
              ...match.location,
              delay: action.payload.delay,
            },
          };
        } else {
          return match;
        }
      });
    },
  },
});

export const {
  setGroupMatches,
  updateGroupMatch,
  createGroupMatch,
  deleteGroupMatch,
  setEditingMatch,
  updateLocationOnAllMatches,
  addEvent,
  deleteEvent,
  updateEvent,
  updateReferees
} = MatchesSlice.actions;

export default MatchesSlice.reducer;
